// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, Mail, MessageSquare, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface TournamentNotificationDialogProps {
  tournamentId: string;
  eventId: string;
  organizationId: string;
  tournamentName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Recipient {
  email: string;
  phone?: string;
  name: string;
  teamName: string;
}

interface Template {
  id: string;
  name: string;
  subject: string;
  content: string;
  html_content: string;
}

const TournamentNotificationDialog: React.FC<TournamentNotificationDialogProps> = ({
  tournamentId,
  eventId,
  organizationId,
  tournamentName,
  open,
  onOpenChange
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [subject, setSubject] = useState(`🏀 Schedule Update: ${tournamentName}`);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [smsSubscribers, setSmsSubscribers] = useState<{ id: string; phone_number: string }[]>([]);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      fetchRecipients();
      fetchSmsSubscribers();
      // Set default body
      setBody(getDefaultBody());
    }
  }, [open, tournamentId]);

  const getDefaultBody = () => `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <p>Hello {{coach_name}},</p>
  
  <p>The game schedule for <strong>${tournamentName}</strong> has been posted!</p>
  
  <p>Your team <strong>{{team_name}}</strong> has been assigned game times. Please log in to view your complete schedule including:</p>
  
  <ul>
    <li>Game times and dates</li>
    <li>Court assignments</li>
    <li>Opponent information</li>
  </ul>
  
  <p style="text-align: center; margin: 30px 0;">
    <a href="{{tournament_link}}" style="background: #002E62; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">
      View Schedule
    </a>
  </p>
  
  <p>Good luck to your team!</p>
  
  <p>Best regards,<br>RecruitLook Events Team</p>
</div>
  `.trim();

  const fetchTemplates = async () => {
    const { data } = await supabase
      .from('communication_templates')
      .select('id, name, subject, content, html_content')
      .eq('is_active', true)
      .order('name');
    setTemplates(data || []);
  };

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      // Get registered teams for this event
      const { data: registrations } = await supabase
        .from('registrations')
        .select('id, registrant_data')
        .eq('event_id', eventId)
        .eq('registration_type', 'team')
        .in('status', ['confirmed', 'pending']);

      const recipientList: Recipient[] = [];
      const seenEmails = new Set<string>();

      registrations?.forEach(reg => {
        const data = reg.registrant_data as any;
        if (!data) return;

        const email = data.coach_email || data.coach?.email;
        const phone = data.coach_phone || data.coach?.phone;
        const name = data.coach_name || data.coach?.name || 'Coach';
        const teamName = data.team_name || data.team?.name || 'Your Team';

        if (email && !seenEmails.has(email)) {
          seenEmails.add(email);
          recipientList.push({ email, phone, name, teamName });
        }
      });

      // Also check coach_teams for teams in the bracket
      const { data: brackets } = await supabase
        .from('tournament_brackets')
        .select('team_id')
        .eq('tournament_id', tournamentId);

      const teamIds = brackets?.map(b => b.team_id).filter(Boolean) || [];

      if (teamIds.length > 0) {
        const { data: coachTeams } = await supabase
          .from('coach_teams')
          .select('id, name, head_coach_email, head_coach_cell, head_coach_name')
          .in('id', teamIds);

        coachTeams?.forEach(team => {
          if (team.head_coach_email && !seenEmails.has(team.head_coach_email)) {
            seenEmails.add(team.head_coach_email);
            recipientList.push({
              email: team.head_coach_email,
              phone: team.head_coach_cell || undefined,
              name: team.head_coach_name || 'Coach',
              teamName: team.name || 'Your Team'
            });
          }
        });
      }

      setRecipients(recipientList);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const fetchSmsSubscribers = async () => {
    const { data } = await supabase
      .from('tournament_notifications')
      .select('id, phone_number')
      .eq('tournament_id', tournamentId)
      .eq('notification_type', 'sms');
    
    // Deduplicate by phone
    const uniquePhones = new Map<string, { id: string; phone_number: string }>();
    data?.forEach(sub => {
      if (sub.phone_number && !uniquePhones.has(sub.phone_number)) {
        uniquePhones.set(sub.phone_number, sub);
      }
    });
    
    setSmsSubscribers(Array.from(uniquePhones.values()));
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    if (templateId === 'custom') {
      setSubject(`🏀 Schedule Update: ${tournamentName}`);
      setBody(getDefaultBody());
      return;
    }
    
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject || `Schedule Update: ${tournamentName}`);
      setBody(template.html_content || template.content || '');
    }
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!body.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (recipients.length === 0 && smsSubscribers.length === 0) {
      toast.error('No recipients found');
      return;
    }

    setSending(true);
    let emailsSent = 0;
    let smsSent = 0;

    try {
      // Send emails to coaches
      if (sendEmail && recipients.length > 0) {
        const emailPromises = recipients.map(recipient => {
          // Replace variables in body
          const personalizedBody = body
            .replace(/\{\{coach_name\}\}/g, recipient.name)
            .replace(/\{\{team_name\}\}/g, recipient.teamName)
            .replace(/\{\{tournament_link\}\}/g, `https://recruitlook.com/tournaments/${tournamentId}/bracket`);

          const personalizedSubject = subject
            .replace(/\{\{team_name\}\}/g, recipient.teamName);

          return invokeEdgeFunction('communication-handler', {
            body: {
              type: 'email',
              action: 'send_immediate',
              organization_id: organizationId,
              recipient: { email: recipient.email },
              content: {
                subject: personalizedSubject,
                body: personalizedBody,
                isHtml: true
              },
              metadata: {
                trigger_source: 'tournament_notification',
                tournament_id: tournamentId,
                event_id: eventId
              }
            }
          });
        });

        const emailResults = await Promise.all(emailPromises);
        emailsSent = emailResults.filter(r => !r.error).length;
      }

      // Send SMS to subscribers
      if (sendSms && smsSubscribers.length > 0) {
        const smsMessage = `🏀 ${tournamentName} schedule is LIVE! View your game times at recruitlook.com/tournaments/${tournamentId}/bracket`;
        
        const smsPromises = smsSubscribers.map(subscriber =>
          invokeEdgeFunction('send-sms', {
            body: {
              to_phone: subscriber.phone_number,
              message: smsMessage
            }
          })
        );

        const smsResults = await Promise.all(smsPromises);
        smsSent = smsResults.filter(r => !r.error).length;
      }

      const messages: string[] = [];
      if (emailsSent > 0) messages.push(`${emailsSent} emails`);
      if (smsSent > 0) messages.push(`${smsSent} SMS`);

      if (messages.length > 0) {
        toast.success(`Sent ${messages.join(' and ')} successfully!`);
      } else {
        toast.warning('No notifications were sent');
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Send error:', error);
      toast.error('Failed to send notifications');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Notify Teams - {tournamentName}
          </DialogTitle>
          <DialogDescription>
            Send schedule updates to registered coaches and SMS subscribers
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Recipient Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-900">Email Recipients</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">{recipients.length}</div>
                <p className="text-xs text-blue-700">Registered coaches</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="h-4 w-4 text-green-600" />
                  <span className="font-medium text-green-900">SMS Subscribers</span>
                </div>
                <div className="text-2xl font-bold text-green-600">{smsSubscribers.length}</div>
                <p className="text-xs text-green-700">Signed up for alerts</p>
              </div>
            </div>

            {/* Channel Selection */}
            <div className="flex gap-6">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="sendEmail" 
                  checked={sendEmail} 
                  onCheckedChange={(checked) => setSendEmail(!!checked)}
                  disabled={recipients.length === 0}
                />
                <Label htmlFor="sendEmail" className="cursor-pointer">
                  Send Email ({recipients.length})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="sendSms" 
                  checked={sendSms} 
                  onCheckedChange={(checked) => setSendSms(!!checked)}
                  disabled={smsSubscribers.length === 0}
                />
                <Label htmlFor="sendSms" className="cursor-pointer">
                  Send SMS ({smsSubscribers.length})
                </Label>
              </div>
            </div>

            {/* Template Selector */}
            <div className="space-y-2">
              <Label>Email Template</Label>
              <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select template or use default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">📝 Default Schedule Update</SelectItem>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Recipients Preview */}
            {recipients.length > 0 && (
              <div className="space-y-2">
                <Label>Recipients Preview</Label>
                <div className="max-h-24 overflow-y-auto bg-muted/50 rounded-md p-3 text-sm space-y-1">
                  {recipients.slice(0, 5).map((r, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Users className="h-3 w-3 text-muted-foreground" />
                      <span>{r.email}</span>
                      <Badge variant="outline" className="text-xs">{r.teamName}</Badge>
                    </div>
                  ))}
                  {recipients.length > 5 && (
                    <div className="text-muted-foreground">...and {recipients.length - 5} more</div>
                  )}
                </div>
              </div>
            )}

            {/* Subject */}
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input 
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Schedule Update: Your Team's Game Times"
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label>Message (HTML)</Label>
              <Textarea 
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
                className="font-mono text-sm"
                placeholder="Write your message here..."
              />
              <p className="text-xs text-muted-foreground">
                Available variables: <code className="bg-muted px-1 rounded">{'{{'+'coach_name'+'}}'}</code>, 
                <code className="bg-muted px-1 rounded ml-1">{'{{'+'team_name'+'}}'}</code>, 
                <code className="bg-muted px-1 rounded ml-1">{'{{'+'tournament_link'+'}}'}</code>
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={sending || loading || (!sendEmail && !sendSms)}
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Notifications
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TournamentNotificationDialog;
